import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router';
import { Game } from '../types/curling';
import { getGame } from '../utils/storage';
import { ArrowLeft, Gauge, ChevronRight, Users } from 'lucide-react';
import { computeSheetScoring } from '../utils/sheetScoring';
import { computePlayerScores, computeTeamScore } from '../utils/playerScoring';

/** The macro landing page for BRC Scoring: headline numbers for the sheet, the
 *  two teams, and the players overall, each linking through to its own detail sub-page. */
export function BRCScoring() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    getGame(gameId)
      .then(foundGame => {
        if (!cancelled && foundGame) setGame(foundGame);
      })
      .catch(error => console.error('Failed to load BRC Scoring data:', error));

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6 flex items-center justify-center">
        <p className="text-blue-300">Loading…</p>
      </div>
    );
  }

  const scoring = computeSheetScoring(game);
  const players = computePlayerScores(game);
  const team1Score = computeTeamScore(players.filter(p => p.team === 'team1'));
  const team2Score = computeTeamScore(players.filter(p => p.team === 'team2'));
  const playerOverallScores = players.map(p => p.overallScore).filter((v): v is number => v !== null);
  const playersAvgScore = playerOverallScores.length > 0
    ? playerOverallScores.reduce((sum, v) => sum + v, 0) / playerOverallScores.length
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          <button
            onClick={() => {
              if (location.key !== 'default') {
                navigate(-1);
              } else {
                navigate(`/CurlingCodex/game/${gameId}`);
              }
            }}
            className="flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3 mb-2">
            <Gauge className="w-6 h-6 text-blue-300" />
            <h1 className="text-2xl font-bold text-white">BRC Scoring</h1>
          </div>
          <p className="text-sm text-blue-300">
            {game.curlingClub}{game.curlingClub && game.sheetNumber && ' • '}{game.sheetNumber && `Sheet ${game.sheetNumber}`}
          </p>
          <p className="text-xs text-slate-400 mt-2 max-w-3xl">
            A sheet- and shot-quality scoring system: how the ice itself played, and — once each
            player is scored individually and rolled up to their team — how each side performed on it.
          </p>
        </div>

        {!scoring.hasData ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <Gauge className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Shots Recorded Yet</h3>
            <p className="text-blue-300">Scoring needs at least a few recorded shots to work with.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Link
              to={`/CurlingCodex/game/${gameId}/brc-scoring/sheet`}
              className="block bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 hover:bg-white/12 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Sheet</h3>
                  <p className="text-xs text-slate-400">Curl and weight consistency across the ice.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-white">{scoring.overallScore.toFixed(0)}%</span>
                  <ChevronRight className="w-5 h-5 text-blue-300" />
                </div>
              </div>
              <div className="flex gap-6 mt-4 pt-4 border-t border-white/10">
                <div>
                  <p className="text-xs text-blue-300 mb-1">Curl</p>
                  <p className="text-lg font-bold text-white">
                    {scoring.compositeCurlScore !== null ? scoring.compositeCurlScore.toFixed(1) : '—'}
                    <span className="text-sm text-blue-300 font-normal"> / 10</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-300 mb-1">Weight</p>
                  <p className="text-lg font-bold text-white">
                    {scoring.compositeWeightScore !== null ? scoring.compositeWeightScore.toFixed(1) : '—'}
                    <span className="text-sm text-blue-300 font-normal"> / 10</span>
                  </p>
                </div>
              </div>
            </Link>

            <Link
              to={`/CurlingCodex/game/${gameId}/brc-scoring/teams`}
              className="block bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 hover:bg-white/12 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-blue-300" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Teams &amp; Players</h3>
                    <p className="text-xs text-slate-400">Weight and curl control, per player, rolled up to each team.</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-blue-300" />
              </div>
              <div className="flex gap-6 mt-4 pt-4 border-t border-white/10">
                <div>
                  <p className="text-xs text-blue-300 mb-1">{game.team1Name || 'Team 1'}</p>
                  <p className="text-lg font-bold text-white">
                    {team1Score.overallScore !== null ? `${team1Score.overallScore.toFixed(0)}%` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-300 mb-1">{game.team2Name || 'Team 2'}</p>
                  <p className="text-lg font-bold text-white">
                    {team2Score.overallScore !== null ? `${team2Score.overallScore.toFixed(0)}%` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-300 mb-1">Players Avg</p>
                  <p className="text-lg font-bold text-white">
                    {playersAvgScore !== null ? `${playersAvgScore.toFixed(0)}%` : '—'}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
